// Pure parser of deploy.sh stdout into retroactive phases.
//
// deploy.sh runs `docker compose pull/build/up` which emits buildkit
// step lines (`#N [service X/Y] desc` + `#N DONE Xs`) and lifecycle
// lines (`Network X Created`, `Container X Started`). We bucket those
// into 4 phase groups suitable for a Timeline display:
//
//   Building <service>  — one phase per service, with summed step time
//   Networking          — single phase summarising network creations
//   Containers          — single phase summarising container starts
//   Concluído           — final marker (success/failed)
//
// Pure / no DOM. Used by DeployTimeline.tsx in the result view.

export type ParsedDeployStatus = 'success' | 'failed' | 'unknown'

export type ParsedPhaseStatus = 'success' | 'failed' | 'running' | 'pending'

export interface ParsedDeployPhase {
  name: string
  status: ParsedPhaseStatus
  durationMs?: number
  /** One-line summary shown next to the phase title */
  summary?: string
  /** Sub-lines available when the phase is expanded */
  details?: string[]
}

export interface ParsedDeploy {
  phases: ParsedDeployPhase[]
  finalStatus: ParsedDeployStatus
}

const BUILD_STEP_RE = /^#(\d+)\s+\[([\w.\-]+)\s+(\d+)\/(\d+)\]\s+(.*)$/
const BUILD_DONE_RE = /^#(\d+)\s+DONE\s+([\d.]+)s/
const SERVICE_BUILT_RE = /^\s*Service\s+([\w.\-]+)\s+Built\b/i
const NETWORK_LINE_RE = /^\s*Network\s+([\w.\-]+)\s+(Creating|Created)\b/i
const CONTAINER_LINE_RE = /^\s*Container\s+([\w.\-]+)\s+(Creating|Created|Starting|Started)\b/i
const FINAL_OK_RE = /\[INFO\]\s+Deploy finalizado com sucesso/i
const FINAL_ERR_RE = /\[ERROR\]/i

interface BuildBucket {
  service: string
  steps: Array<{ id: string; description: string; durationMs?: number }>
}

export function parseDeployOutput(input: string): ParsedDeploy {
  const lines = input.split('\n')

  const buildById = new Map<string, { service: string; description: string }>()
  const buildBucketsOrder: string[] = []
  const buildBuckets = new Map<string, BuildBucket>()
  const buildDone = new Map<string, number>() // ms per build id

  const networks = new Set<string>()
  const networkLines: string[] = []
  const containers = new Set<string>()
  const containerLines: string[] = []
  let finalStatus: ParsedDeployStatus = 'unknown'

  for (const raw of lines) {
    const line = raw.replace(/\r/g, '').trimEnd()
    if (!line) continue

    if (FINAL_OK_RE.test(line)) {
      finalStatus = 'success'
      continue
    }
    if (FINAL_ERR_RE.test(line)) {
      finalStatus = 'failed'
      continue
    }

    const buildMatch = BUILD_STEP_RE.exec(line)
    if (buildMatch) {
      const id = buildMatch[1]
      const service = buildMatch[2]
      const desc = `${buildMatch[3]}/${buildMatch[4]} ${buildMatch[5]}`
      buildById.set(id, { service, description: desc })
      if (!buildBuckets.has(service)) {
        buildBucketsOrder.push(service)
        buildBuckets.set(service, { service, steps: [] })
      }
      buildBuckets.get(service)!.steps.push({ id, description: desc })
      continue
    }

    const doneMatch = BUILD_DONE_RE.exec(line)
    if (doneMatch) {
      const id = doneMatch[1]
      const seconds = parseFloat(doneMatch[2])
      buildDone.set(id, Math.round(seconds * 1000))
      // Attach duration to the corresponding bucket step
      const meta = buildById.get(id)
      if (meta) {
        const bucket = buildBuckets.get(meta.service)!
        const step = bucket.steps.find(s => s.id === id)
        if (step) step.durationMs = Math.round(seconds * 1000)
      }
      continue
    }

    if (SERVICE_BUILT_RE.test(line)) {
      // already collected via BUILD_STEP markers; nothing extra to do
      continue
    }

    const netMatch = NETWORK_LINE_RE.exec(line)
    if (netMatch) {
      if (netMatch[2].toLowerCase() === 'created') networks.add(netMatch[1])
      networkLines.push(line.trim())
      continue
    }

    const conMatch = CONTAINER_LINE_RE.exec(line)
    if (conMatch) {
      if (conMatch[2].toLowerCase() === 'started') containers.add(conMatch[1])
      containerLines.push(line.trim())
      continue
    }
  }

  const phases: ParsedDeployPhase[] = []

  // Build phases (one per service, in first-seen order)
  for (const service of buildBucketsOrder) {
    const bucket = buildBuckets.get(service)!
    const totalMs = bucket.steps.reduce((acc, s) => acc + (s.durationMs ?? 0), 0)
    phases.push({
      name: `Building ${service}`,
      status: 'success',
      durationMs: totalMs,
      summary: `${bucket.steps.length} step${bucket.steps.length === 1 ? '' : 's'}`,
      details: bucket.steps.map(s =>
        s.durationMs != null
          ? `[${s.id}] ${s.description} (${(s.durationMs / 1000).toFixed(1)}s)`
          : `[${s.id}] ${s.description}`,
      ),
    })
  }

  if (networks.size > 0) {
    phases.push({
      name: 'Networking',
      status: 'success',
      summary: `${networks.size} network${networks.size === 1 ? '' : 's'} criada${networks.size === 1 ? '' : 's'}`,
      details: networkLines,
    })
  }

  if (containers.size > 0) {
    phases.push({
      name: 'Containers',
      status: 'success',
      summary: [...containers].join(', '),
      details: containerLines,
    })
  }

  if (finalStatus !== 'unknown') {
    phases.push({
      name: 'Concluído',
      status: finalStatus === 'success' ? 'success' : 'failed',
      summary:
        finalStatus === 'success'
          ? 'Deploy finalizado com sucesso'
          : 'Deploy abortado por erro',
    })
  }

  return { phases, finalStatus }
}
